import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Send as SendIcon,
  Stop as StopIcon,
  AttachFile as AttachIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import { MessageInputProps, FileAttachment } from '../../types/message';
import { FileAttachment as FileAttachmentComponent } from './FileAttachment';
import { MESSAGE_LIMITS } from '../../types/message';

/**
 * メッセージ入力フォームコンポーネント
 * テキスト入力、ファイル添付、送信機能を提供
 */
export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onStopMessage,
  isLoading = false,
  isAborting = false,
  placeholder = 'テキストメッセージを入力してください...',
  maxLength = MESSAGE_LIMITS.MAX_LENGTH,
  allowFileUpload = true,
  maxFileSize = MESSAGE_LIMITS.MAX_FILE_SIZE,
  acceptedFileTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'],
  disabled = false,
  onHeightChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const [showFileAttachment, setShowFileAttachment] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // メッセージ送信
  const handleSendMessage = useCallback(async () => {
    const trimmedMessage = message.trim();
    
    // テキストメッセージは必須
    if (!trimmedMessage) {
      return;
    }

    // エラーのあるファイルは除外
    const validFiles = files
      .filter(f => !f.error)
      .map(f => f.file);

    const messageToSend = trimmedMessage;
    const filesToSend = validFiles.length > 0 ? validFiles : undefined;
    
    try {
      // メッセージ送信
      await onSendMessage(messageToSend, filesToSend);
      
      // 送信成功後にフォームをリセット
      setMessage('');
      setFiles([]);
      setShowFileAttachment(false);
    } catch (error) {
      // 送信失敗時はフォームを保持（ユーザーが再試行可能）
      console.error('メッセージの送信に失敗しました:', error);
      // エラーハンドリングは親コンポーネントで行う想定
    }
  }, [message, files, onSendMessage]);

  // メッセージ送信停止
  const handleStopMessage = useCallback(() => {
    if (onStopMessage) {
      onStopMessage();
    }
  }, [onStopMessage]);

  // IME入力開始
  const handleCompositionStart = useCallback(() => {
    setIsComposing(true);
  }, []);

  // IME入力終了
  const handleCompositionEnd = useCallback(() => {
    setIsComposing(false);
  }, []);

  // キーボードショートカット
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      // IME入力中は送信しない
      if (isComposing) {
        return;
      }
      
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        handleSendMessage();
      } else if (e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
      // Enter単体では改行（デフォルト動作のまま）
    }
  }, [handleSendMessage, isComposing]);

  // 文字数カウント
  const characterCount = message.length;
  const isOverLimit = characterCount > maxLength;
  const isNearLimit = characterCount > maxLength * 0.8;

  // 送信可能性チェック（テキストメッセージ必須）
  const canSend = !isLoading && 
                  !disabled && 
                  !isOverLimit && 
                  message.trim().length > 0;

  // ファイル添付の表示/非表示切り替え
  const toggleFileAttachment = useCallback(() => {
    setShowFileAttachment(prev => !prev);
  }, []);

  // テキストエリアの自動リサイズ
  useEffect(() => {
    const textField = textFieldRef.current;
    if (textField) {
      textField.style.height = 'auto';
      textField.style.height = `${Math.min(textField.scrollHeight, 120)}px`;
    }
  }, [message]);

  // 高さ変更の監視と通知
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onHeightChange) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        onHeightChange(height);
      }
    });

    observer.observe(container);
    
    // 初期高さも通知
    const initialHeight = container.offsetHeight;
    onHeightChange(initialHeight);

    return () => {
      observer.disconnect();
    };
  }, [onHeightChange, showFileAttachment, files.length, message]); // 高さに影響する依存関係を追加

  return (
    <Box
      ref={containerRef}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
        transition: 'box-shadow 0.2s ease',
        overflow: 'hidden',
        '&:focus-within': {
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      {/* ファイル添付エリア */}
      {allowFileUpload && (
        <Collapse in={showFileAttachment}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <FileAttachmentComponent
              files={files}
              onFilesChange={setFiles}
              maxFileSize={maxFileSize}
              acceptedTypes={acceptedFileTypes.map(type => `.${type}`)}
              disabled={disabled || isLoading}
            />
          </Box>
        </Collapse>
      )}

      {/* メイン入力エリア */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', p: 1 }}>
        {/* ファイル添付ボタン */}
        {allowFileUpload && (
          <Tooltip title={showFileAttachment ? 'ファイル添付を閉じる' : 'ファイルを添付'}>
            <IconButton
              onClick={toggleFileAttachment}
              disabled={disabled || isLoading}
              sx={{
                color: showFileAttachment ? theme.palette.primary.main : theme.palette.grey[500],
                mb: 0.5,
              }}
            >
              {showFileAttachment ? <CollapseIcon /> : <AttachIcon />}
            </IconButton>
          </Tooltip>
        )}

        {/* テキスト入力フィールド */}
        <TextField
          inputRef={textFieldRef}
          multiline
          minRows={1}
          maxRows={5}
          fullWidth
          variant="standard"
          placeholder={placeholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          disabled={disabled || isLoading}
          error={isOverLimit}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: '0.95rem',
              lineHeight: 1.5,
              px: 1,
              py: 0.5,
            },
          }}
          sx={{
            '& .MuiInputBase-input': {
              resize: 'none',
              '&::placeholder': {
                color: theme.palette.grey[500],
                opacity: 1,
              },
            },
          }}
        />

        {/* 送信ボタン / 停止ボタン */}
        {isLoading || isAborting ? (
          /* 停止ボタン */
          <Tooltip title={isAborting ? 'メッセージ送信を停止中...' : 'メッセージ送信を停止'}>
            <span>
              <IconButton
                onClick={handleStopMessage}
                disabled={isAborting || !onStopMessage}
                sx={{
                  color: theme.palette.error.main,
                  backgroundColor: theme.palette.error.light + '20',
                  mb: 0.5,
                  ...(isMobile && {
                    minWidth: '48px',
                    minHeight: '48px',
                  }),
                  '&:hover': {
                    backgroundColor: theme.palette.error.light + '40',
                  },
                  '&.Mui-disabled': {
                    color: theme.palette.grey[400],
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <StopIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          /* 送信ボタン */
          <Tooltip 
            title={
              canSend 
                ? isMobile 
                  ? 'メッセージを送信' 
                  : 'メッセージを送信 (Shift+Enter)' 
                : isOverLimit 
                ? '文字数制限を超えています' 
                : 'テキストメッセージを入力してください'
            }
          >
            <span>
              <IconButton
                onClick={handleSendMessage}
                disabled={!canSend}
                sx={{
                  color: canSend ? theme.palette.primary.main : theme.palette.grey[400],
                  backgroundColor: canSend ? theme.palette.primary.light + '20' : 'transparent',
                  mb: 0.5,
                  ...(isMobile && {
                    minWidth: '48px',
                    minHeight: '48px',
                  }),
                  '&:hover': {
                    backgroundColor: canSend ? theme.palette.primary.light + '40' : 'transparent',
                  },
                }}
              >
                <SendIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* フッター情報 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 0.75,
          backgroundColor: theme.palette.grey[50],
          borderTop: `1px solid ${theme.palette.divider}`,
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: theme.palette.grey[900],
          }),
        }}
      >
        <Typography variant="caption" color="textSecondary">
          {isMobile ? 
            'Enter: 改行 | 送信: ボタンをタップ' : 
            'Enter: 改行 | Shift+Enter: 送信'
          }
        </Typography>
        
        <Typography 
          variant="caption" 
          color={isOverLimit ? 'error' : isNearLimit ? 'warning.main' : 'textSecondary'}
        >
          {characterCount}/{maxLength}
        </Typography>
      </Box>
    </Box>
  );
};